import React, { useState, useEffect, useCallback } from "react";
import { Box, Button, Alert, useMediaQuery, useTheme } from "@mui/material";
import LockOpenIcon from "@mui/icons-material/LockOpen";

import PageContainer from "../layouts/PageContainer";
import AppCircularProgress from "../components/common/AppCircularProgress";
import PosMobileView from "../components/pos/PosMobileView";
import PosDesktopView from "../components/pos/PosDesktopView";
import ReceiptPreviewModal from "../components/pos/ReceiptPreviewModal";

import useWorkspaceNavigation from "../hooks/useWorkspaceNavigation";
import useDebounce from "../hooks/useDebounce";

import { useAppDispatch, useAppSelector } from "../redux/store";
import {
  checkCashierHasOpenShift,
  getCurrentOrRecentShift,
} from "../redux/actions/shiftActions";
import { getItems } from "../redux/actions/itemActions";
import { getCustomers } from "../redux/actions/customerActions";
import { getPaymentMethods } from "../redux/actions/paymentMethodActions";
import { getCurrencies } from "../redux/actions/currencyActions";
import { getCompanyCharges } from "../redux/actions/companyChargeActions";
import { getBranchSettings } from "../redux/actions/settingsActions";
import { resetReceiptServerStatus } from "../redux/actions/receiptActions";

const PosPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { navigateToTab } = useWorkspaceNavigation();

  const { hasOpenShift, loadingHasOpenShift } = useAppSelector(
    (state) => state.shift,
  );
  const {
    items,
    loadingFetchAll: loadingItems,
    errorFetchAll: errorLoadingItems,
  } = useAppSelector((state) => state.item);
  const { serverStatus } = useAppSelector((state) => state.receipt);

  const [pageConfig, setPageConfig] = useState({ page: 1, limit: 10 });
  const [searchTerm, setSearchTerm] = useState<string>("");
  const debouncedSearch = useDebounce<string>(searchTerm, 500);

  const [isPreviewModalOpen, setIsPreviewModalOpen] = useState<boolean>(false);

  // 1. Initial Checks & Dependencies
  useEffect(() => {
    dispatch(checkCashierHasOpenShift());
    dispatch(getCurrentOrRecentShift());
    dispatch(getCompanyCharges());
    dispatch(getBranchSettings());
    dispatch(getCustomers({ size: 20 }));
    dispatch(getPaymentMethods({ size: 20 }));
    dispatch(getCurrencies());
  }, [dispatch]);

  // 2. Fetch Items
  const fetchItemsData = useCallback(() => {
    dispatch(
      getItems({
        page: pageConfig.page,
        size: pageConfig.limit,
        search: debouncedSearch,
      }),
    );
  }, [dispatch, pageConfig.page, pageConfig.limit, debouncedSearch]);

  useEffect(() => {
    if (hasOpenShift) fetchItemsData();
  }, [hasOpenShift, fetchItemsData]);

  useEffect(() => {
    setPageConfig((p) => ({ ...p, page: 1 }));
  }, [debouncedSearch]);

  // 3. Handle Success close
  useEffect(() => {
    if (serverStatus?.operation === "create" && serverStatus?.status === 201) {
      setIsPreviewModalOpen(false);
      dispatch(resetReceiptServerStatus());
    }
  }, [serverStatus, dispatch]);

  // Handle Shift Requirement
  if (loadingHasOpenShift) return <AppCircularProgress />;
  if (!hasOpenShift) {
    return (
      <PageContainer title="Point of Sale">
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            height: "60vh",
          }}
        >
          <Alert severity="warning" sx={{ mb: 3, maxWidth: 500 }}>
            No active shift found. An open shift is required to process
            transactions.
          </Alert>
          <Button
            variant="contained"
            color="secondary"
            size="large"
            startIcon={<LockOpenIcon />}
            onClick={() => navigateToTab("/shifts")}
          >
            Go to Shifts Page to Open
          </Button>
        </Box>
      </PageContainer>
    );
  }

  // Render POS Layout
  return (
    <Box
      sx={{
        p: isMobile ? 0 : 2,
        height: "calc(100vh - 200px)",
        overflow: "hidden",
      }}
    >
      {isMobile ? (
        <PosMobileView
          items={items}
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onOpenPreview={() => setIsPreviewModalOpen(true)}
        />
      ) : (
        <PosDesktopView
          items={items}
          loading={loadingItems}
          error={errorLoadingItems}
          onRetry={fetchItemsData}
          pagination={pageConfig}
          onPageChange={(p) => setPageConfig((c) => ({ ...c, page: p }))}
          onLimitChange={(l) =>
            setPageConfig((c) => ({ ...c, limit: l, page: 1 }))
          }
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          onOpenPreview={() => setIsPreviewModalOpen(true)}
        />
      )}

      {isPreviewModalOpen && (
        <ReceiptPreviewModal
          open={isPreviewModalOpen}
          onClose={() => setIsPreviewModalOpen(false)}
        />
      )}
    </Box>
  );
};

export default PosPage;
